import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { KINDS, KIND_LABEL, KIND_SHORT, STATUS, STATUSES, OPEN, buildGrid, filterGrid, gridTotals, gridTable } from '../lib/fleetStandards'
import { sectionsFile, exportName } from '../lib/exportCore'

// Fleet -> Standards (fleet-3, sql/fleet-3.sql): the company library and the adoption grid. Management saves a standard from a record on
// one of its vessels (versioned), sends it to vessels (each gets its own Draft; the Master sets it Active on board - management never
// can), and sees vessel x standard: Not sent / Draft / Active / Changed locally / Review overdue. A cell opens that copy in the vessel.
const CSS = C => `
.hcs-bar{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:12px}
.hcs-chip{border:1px solid ${C.line};background:${C.card};color:${C.muted};border-radius:18px;padding:7px 13px;font-weight:600;font-size:12px;line-height:1;font-family:inherit;cursor:pointer;white-space:nowrap}
.hcs-chip.on{background:${C.blue};border-color:${C.blue};color:#fff}
.hcs-chip em{font-style:normal;margin-left:6px;opacity:.85}
.hcs-btn{background:${C.blue};border:1px solid ${C.blue};color:#fff;border-radius:9px;padding:8px 14px;font:600 12.5px/1 inherit;font-family:inherit;cursor:pointer;white-space:nowrap}
.hcs-btn:disabled{opacity:.45;cursor:default}
.hcs-ghost{background:transparent;border:1px solid ${C.line};color:${C.text};border-radius:8px;padding:6px 10px;font:600 11.5px/1 inherit;font-family:inherit;cursor:pointer;white-space:nowrap}
.hcs-ghost:hover{border-color:${C.blue}}
.hcs-kpis{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px;margin-bottom:14px}
.hcs-k{background:${C.card};border:1px solid ${C.line};border-radius:12px;padding:11px 14px;cursor:pointer;text-align:left;font-family:inherit;color:${C.text}}
.hcs-k.on{border-color:${C.blue};box-shadow:inset 0 0 0 1px ${C.blue}}
.hcs-k b{display:block;font-size:22px;font-variant-numeric:tabular-nums}
.hcs-k span{font-size:11.5px;color:${C.muted}}
.hcs-card{background:${C.card};border:1px solid ${C.line};border-radius:14px;padding:6px 14px 12px}
.hcs-row{display:grid;grid-template-columns:var(--cols);gap:10px;align-items:center;padding:10px 4px;border-top:1px solid ${C.line}}
.hcs-head{border-top:none;font-size:11px;color:${C.muted};font-weight:700;padding-top:10px}
.hcs-head div{overflow-wrap:anywhere;line-height:1.25}
.hcs-name{font-weight:600;font-size:13px;overflow-wrap:anywhere;line-height:1.3}
.hcs-sub{font-size:11.5px;color:${C.muted};margin-top:3px;overflow-wrap:anywhere}
.hcs-acts{display:flex;gap:6px;margin-top:7px;flex-wrap:wrap}
.hcs-cell{display:flex;flex-direction:column;align-items:flex-start;gap:3px;background:none;border:none;padding:4px 2px;cursor:pointer;font-family:inherit;text-align:left;border-radius:8px;min-width:0}
.hcs-cell:hover{background:rgba(59,123,255,.1)}
.hcs-cell small{font-size:10.5px;color:${C.muted};line-height:1.25;overflow-wrap:anywhere}
.hcs-pill{display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:700;padding:3px 9px;border-radius:11px;white-space:nowrap}
.hcs-pill i{width:6px;height:6px;border-radius:50%;display:inline-block}
.hcs-vn{display:none}
.hcs-cells{display:contents}
.hcs-panel{background:var(--bg);border:1px solid ${C.blue}55;border-radius:14px;padding:16px;margin-bottom:14px}
.hcs-panel h3{margin:0 0 4px;font-size:15px}
.hcs-panel p{margin:0 0 12px;font-size:12px;color:${C.muted};line-height:1.45}
.hcs-f{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-bottom:10px}
.hcs-f label{display:flex;flex-direction:column;gap:5px;font-size:11.5px;color:${C.muted};font-weight:600;min-width:0}
.hcs-in{background:${C.card};color:${C.text};border:1px solid ${C.line};border-radius:9px;padding:8px 10px;font:500 12.5px/1.2 inherit;font-family:inherit;min-width:0;width:100%;box-sizing:border-box}
.hcs-list{max-height:260px;overflow-y:auto;border:1px solid ${C.line};border-radius:10px;margin-bottom:10px}
.hcs-opt{display:flex;gap:10px;align-items:flex-start;padding:9px 11px;border-top:1px solid ${C.line};cursor:pointer;font-size:12.5px}
.hcs-opt:first-child{border-top:none}
.hcs-opt.on{background:rgba(59,123,255,.16)}
.hcs-opt input{margin-top:2px}
.hcs-msg{font-size:12px;margin-top:8px;line-height:1.45}
.hcs-exp{position:relative;margin-left:auto}
.hcs-menu{position:absolute;right:0;top:calc(100% + 4px);z-index:20;background:${C.card};border:1px solid ${C.line};border-radius:10px;box-shadow:0 10px 30px rgba(0,0,0,.4);overflow:hidden;min-width:150px}
.hcs-menu button{display:block;width:100%;text-align:left;background:none;border:none;color:${C.text};padding:10px 14px;font-weight:600;font-size:12.5px;line-height:1;font-family:inherit;cursor:pointer}
.hcs-menu button:hover{background:rgba(59,123,255,.14)}
@media (max-width:1023px){.hcs-kpis{grid-template-columns:repeat(3,minmax(0,1fr))}}
@media (max-width:760px){.hcs-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}.hcs-head{display:none}.hcs-row{grid-template-columns:minmax(0,1fr)!important;gap:6px}
  .hcs-cells{display:grid;grid-template-columns:minmax(0,1fr);gap:2px}.hcs-cell{flex-direction:row;flex-wrap:wrap;align-items:center;width:100%;padding:6px 4px}
  .hcs-vn{display:block;font-size:12px;font-weight:600;color:${C.text};flex:1 1 auto;min-width:0}.hcs-cell small{flex-basis:100%}.hcs-f{grid-template-columns:minmax(0,1fr)}.hcs-exp{margin-left:0}}
`
const TONE = C => ({ muted: C.muted, amber: C.amber, green: C.green, purple: C.purple, red: C.red })
const localToday = () => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') }
const errText = e => String((e && e.message) || e || '').replace(/^.*?ERROR:\s*/, '')
const missing = e => /company_standard|hc_standard_|schema cache|does not exist|42P01|PGRST20/i.test(String((e && (e.message || e.code)) || ''))

// the records of one vessel a standard can be saved from
async function recordsOf(kind, vid) {
  const q = kind === 'ra' ? supabase.from('risk_assessments').select('id,ra_number,activity,status').eq('client_id', vid).order('ra_number').limit(500)
    : kind === 'checklist' ? supabase.from('checklists').select('id,title,category').eq('client_id', vid).order('title').limit(500)
    : supabase.from('manuals').select('id,title,doc_number,status').eq('client_id', vid).eq('doc_kind', kind === 'sop' ? 'sop' : 'manual').is('archived_at', null).order('title').limit(500)
  const { data, error } = await q
  if (error) throw error
  return (data || []).filter(r => kind !== 'ra' || !/archiv/i.test(r.status || '')).map(r => ({
    id: r.id, title: kind === 'ra' ? (r.activity || 'Risk assessment') : r.title,
    sub: kind === 'ra' ? [r.ra_number, r.status].filter(Boolean).join(' · ') : kind === 'checklist' ? r.category || '' : [r.doc_number, r.status].filter(Boolean).join(' · '),
  }))
}

function SavePanel({ fleet, std, lastSource, palette: C, onDone, onClose }) {
  const [vid, setVid] = useState((lastSource && lastSource.client) || (fleet[0] && fleet[0].id) || '')
  const [kind, setKind] = useState(std ? std.kind : 'ra')
  const [recs, setRecs] = useState(null)
  const [pick, setPick] = useState(lastSource ? lastSource.id : '')
  const [q, setQ] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null)
  useEffect(() => {
    let live = true
    setRecs(null)
    if (vid) recordsOf(kind, vid).then(r => { if (live) setRecs(r) }, e => { if (live) { setRecs([]); setMsg({ bad: true, text: errText(e) }) } })
    return () => { live = false }
  }, [vid, kind])
  const shown = (recs || []).filter(r => !q || (r.title + ' ' + r.sub).toLowerCase().includes(q.toLowerCase()))
  async function save() {
    setBusy(true); setMsg(null)
    const { data, error } = await supabase.rpc('hc_standard_save', { p_kind: kind, p_source: pick, p_standard: std ? std.id : null, p_note: note || null })
    setBusy(false)
    if (error) return setMsg({ bad: true, text: missing(error) ? 'Company standards need sql/fleet-3.sql in the database.' : errText(error) })
    onDone(data)
  }
  return (
    <div className="hcs-panel" data-panel="save">
      <h3>{std ? 'New version of ' + (std.code ? std.code + ' ' : '') + std.title : 'Add a company standard'}</h3>
      <p>{std ? 'Pick the record that holds the new text (usually the one you edited). It is saved as version ' + (std.current_version + 1) + '; vessels keep their copy until you send it and their Master sets it Active.'
        : 'Pick a risk assessment, SOP, checklist or written manual on one of your vessels. Its content is saved in the company library as version 1, with a company number.'}</p>
      <div className="hcs-f">
        <label>Vessel<select className="hcs-in" value={vid} onChange={e => { setVid(e.target.value); setPick('') }}>{fleet.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}</select></label>
        <label>Type<select className="hcs-in" value={kind} disabled={!!std} onChange={e => { setKind(e.target.value); setPick('') }}>{KINDS.map(k => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}</select></label>
        <label>Find<input className="hcs-in" value={q} onChange={e => setQ(e.target.value)} placeholder="Words of the title" /></label>
      </div>
      <div className="hcs-list" role="listbox" aria-label="Records">
        {recs == null ? <div className="hcs-opt" style={{ color: C.muted }}>Reading&hellip;</div>
          : !shown.length ? <div className="hcs-opt" style={{ color: C.muted }}>No {KIND_LABEL[kind].toLowerCase()} on this vessel{q ? ' matching "' + q + '"' : ''}.</div>
          : shown.map(r => (
            <div key={r.id} className={'hcs-opt' + (pick === r.id ? ' on' : '')} onClick={() => setPick(r.id)} role="option" aria-selected={pick === r.id}>
              <input type="radio" readOnly checked={pick === r.id} /><div style={{ minWidth: 0 }}><div style={{ fontWeight: 600 }}>{r.title}</div>{r.sub && <div className="hcs-sub" style={{ marginTop: 1 }}>{r.sub}</div>}</div>
            </div>))}
      </div>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 11.5, color: C.muted, fontWeight: 600, marginBottom: 10 }}>What changed (optional)<input className="hcs-in" value={note} onChange={e => setNote(e.target.value)} placeholder={std ? 'e.g. Swell limit added' : 'e.g. First issue'} maxLength={500} /></label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="hcs-btn" disabled={!pick || busy} onClick={save}>{busy ? 'Saving…' : std ? 'Save version ' + (std.current_version + 1) : 'Save to the company library'}</button>
        <button className="hcs-ghost" onClick={onClose}>Cancel</button>
      </div>
      {msg && <div className="hcs-msg" style={{ color: msg.bad ? C.red : C.green }}>{msg.text}</div>}
    </div>
  )
}

function SendPanel({ std, row, fleet, preselect, palette: C, onDone, onClose }) {
  const T = TONE(C)
  const holds = v => { const c = row && row.cells[v.id]; return c && c.version === std.current_version }
  const [sel, setSel] = useState(() => new Set(preselect ? [preselect] : fleet.filter(v => !holds(v)).map(v => v.id)))
  const [busy, setBusy] = useState(false)
  const [res, setRes] = useState(null)
  const [err, setErr] = useState('')
  const flip = id => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  async function send() {
    setBusy(true); setErr('')
    const { data, error } = await supabase.rpc('hc_standard_send', { p_standard: std.id, p_vessels: [...sel] })
    setBusy(false)
    if (error) return setErr(missing(error) ? 'Company standards need sql/fleet-3.sql in the database.' : errText(error))
    setRes(data || [])
    onDone()
  }
  return (
    <div className="hcs-panel" data-panel="send">
      <h3>Send {std.code ? std.code + ' ' : ''}{std.title} &middot; v{std.current_version}</h3>
      <p>Each vessel gets its own <b>Draft</b>. The Master reviews it and sets it Active on board &ndash; management cannot set it Active. A vessel that has an older version keeps it until then; nothing on board is overwritten.</p>
      {!res ? <>
        <div className="hcs-list">
          {fleet.map(v => { const c = row && row.cells[v.id]; const s = c ? STATUS[c.status] : STATUS.not_sent; return (
            <div key={v.id} className={'hcs-opt' + (sel.has(v.id) ? ' on' : '')} onClick={() => flip(v.id)} role="checkbox" aria-checked={sel.has(v.id)}>
              <input type="checkbox" readOnly checked={sel.has(v.id)} />
              <div style={{ minWidth: 0, flex: 1 }}><div style={{ fontWeight: 600 }}>{v.vessel_name}</div><div className="hcs-sub" style={{ marginTop: 1 }}>{holds(v) ? 'Already has v' + std.current_version + ' (skipped)' : c && c.detail ? c.detail : 'Not sent yet'}</div></div>
              <span className="hcs-pill" style={{ background: T[s.tone] + '22', color: T[s.tone] }}><i style={{ background: T[s.tone] }} />{s.label}</span>
            </div>) })}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="hcs-btn" disabled={!sel.size || busy} onClick={send}>{busy ? 'Sending…' : 'Send to ' + sel.size + ' vessel' + (sel.size === 1 ? '' : 's')}</button>
          <button className="hcs-ghost" onClick={onClose}>Cancel</button>
        </div>
      </> : <>
        {res.map(r => <div key={r.client_id} className="hcs-msg" style={{ color: r.result === 'sent' ? C.green : C.muted }}>{r.vessel}: {r.result === 'sent' ? 'Draft sent - waiting for the Master' : 'already has this version'}</div>)}
        <div style={{ marginTop: 10 }}><button className="hcs-ghost" onClick={onClose}>Done</button></div>
      </>}
      {err && <div className="hcs-msg" style={{ color: C.red }}>{err}</div>}
    </div>
  )
}

export default function FleetStandards({ fleet, palette: C, onOpenVessel }) {
  const T = TONE(C)
  const [data, setData] = useState(null)        // { standards, versions, copies } | { missing: true } | { error }
  const [kind, setKind] = useState('all')
  const [status, setStatus] = useState('all')
  const [panel, setPanel] = useState(null)      // { type: 'save', std? } | { type: 'send', std, vid? }
  const [menu, setMenu] = useState(false)
  const [note, setNote] = useState('')
  const [tick, setTick] = useState(0)
  const reload = () => setTick(t => t + 1)

  useEffect(() => {
    let live = true
    ;(async () => {
      const [s, v, c] = await Promise.all([
        supabase.from('company_standards').select('*').order('created_at'),
        supabase.from('company_standard_versions').select('standard_id,version,note,created_at,created_by,source_client_id,source_id').order('version'),
        supabase.rpc('hc_standard_copies_state'),
      ])
      if (!live) return
      const e = s.error || v.error || c.error
      if (e) return setData(missing(e) ? { missing: true } : { error: errText(e) })
      setData({ standards: s.data || [], versions: v.data || [], copies: c.data || [] })
    })()
    return () => { live = false }
  }, [tick])

  const today = localToday()
  const grid = useMemo(() => (data && data.standards ? buildGrid(data.standards, fleet, data.copies, today) : []), [data, fleet, today])
  const shown = filterGrid(grid, kind, status)
  const totals = gridTotals(filterGrid(grid, kind))
  const lastOf = id => (data && data.versions ? data.versions.filter(x => x.standard_id === id).slice(-1)[0] : null)
  const COLS = 'minmax(220px,1.5fr) ' + fleet.map(() => 'minmax(0,1fr)').join(' ')

  function openCell(v, row, cell) {
    if (!cell.copy) return setPanel({ type: 'send', std: row.standard, vid: v.id })
    const o = OPEN[row.standard.kind]
    try { localStorage.setItem(o.key, cell.copy.local_id); Object.entries(o.extra || {}).forEach(([k, x]) => localStorage.setItem(k, x)) } catch (e) {}
    if (onOpenVessel) onOpenVessel(v, o.page)
  }
  async function exportAs(fmt) {
    setMenu(false)
    try {
      if (!shown.length) throw new Error('Nothing to export - no standard in this filter.')
      const file = await sectionsFile([{ name: 'Standards adoption', table: gridTable(shown, fleet) }], fmt)
      const url = URL.createObjectURL(new Blob([file.bytes], { type: file.mime }))
      const a = document.createElement('a'); a.href = url; a.download = exportName('Company standards adoption', file.ext); document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 4000)
    } catch (e) { alert('Could not export: ' + (e.message || e)) }
  }

  if (!data) return <div className="hcs-card" style={{ color: C.muted, fontSize: 12.5, padding: 18 }}><style>{CSS(C)}</style>Reading the company library&hellip;</div>
  if (data.missing) return <div className="hcs-card" style={{ color: C.muted, fontSize: 13, padding: 22, lineHeight: 1.5 }}><style>{CSS(C)}</style><b style={{ color: C.text }}>Company standards are not switched on yet.</b><br />They need <code>sql/fleet-3.sql</code> in the database (the company library, Send to vessels and the adoption grid).</div>
  if (data.error) return <div className="hcs-card" style={{ color: C.red, fontSize: 13, padding: 22 }}><style>{CSS(C)}</style>Could not read the company library: {data.error}</div>

  const panelStd = panel && panel.std
  return (
    <div>
      <style>{CSS(C)}</style>
      <div className="hcs-kpis">
        {STATUSES.map(k => (
          <button key={k} className={'hcs-k' + (status === k ? ' on' : '')} onClick={() => setStatus(s => (s === k ? 'all' : k))} data-status={k}>
            <b style={{ color: totals[k] ? T[STATUS[k].tone] : C.faint }}>{totals[k]}</b><span>{STATUS[k].label}</span>
          </button>))}
      </div>
      <div className="hcs-bar">
        {[['all', 'All']].concat(KINDS.map(k => [k, KIND_SHORT[k]])).map(([k, l]) => (
          <button key={k} className={'hcs-chip' + (kind === k ? ' on' : '')} onClick={() => setKind(k)}>{l}<em>{k === 'all' ? grid.length : grid.filter(r => r.standard.kind === k).length}</em></button>))}
        {status !== 'all' && <button className="hcs-chip on" onClick={() => setStatus('all')} title="Show every status">{STATUS[status].label} &times;</button>}
        <span className="hcs-exp">
          <button className="hcs-chip" onClick={() => setMenu(m => !m)}>Export &#9662;</button>
          {menu && <div className="hcs-menu" role="menu"><button role="menuitem" onClick={() => exportAs('xlsx')}>Excel (.xlsx)</button><button role="menuitem" onClick={() => exportAs('csv')}>CSV</button></div>}
        </span>
        <button className="hcs-btn" onClick={() => { setNote(''); setPanel({ type: 'save' }) }}>+ Add a standard</button>
      </div>
      {note && <div className="hcs-msg" style={{ color: C.green, margin: '-4px 0 12px' }}>{note}</div>}
      {panel && panel.type === 'save' && <SavePanel key={panelStd ? panelStd.id : 'new'} fleet={fleet} std={panelStd} palette={C}
        lastSource={panelStd && lastOf(panelStd.id) ? { client: lastOf(panelStd.id).source_client_id, id: lastOf(panelStd.id).source_id } : null}
        onClose={() => setPanel(null)} onDone={r => { reload(); const s = (data.standards || []).find(x => x.id === r.standard_id); setPanel({ type: 'send', std: { ...(s || { id: r.standard_id, title: 'Standard', kind: panelStd ? panelStd.kind : 'ra' }), code: r.code, current_version: r.version } }); setNote('Saved ' + (r.code || '') + ' version ' + r.version + '. Now choose the vessels to send it to.') }} />}
      {panel && panel.type === 'send' && <SendPanel key={panelStd.id + ':' + panelStd.current_version + ':' + (panel.vid || '')} std={panelStd} row={grid.find(r => r.standard.id === panelStd.id)} fleet={fleet} preselect={panel.vid} palette={C}
        onClose={() => { setPanel(null); setNote('') }} onDone={reload} />}

      <div className="hcs-card" data-grid="standards">
        {!grid.length ? <div style={{ color: C.muted, fontSize: 13, padding: '18px 6px', lineHeight: 1.5 }}>No company standard yet. <b style={{ color: C.text }}>+ Add a standard</b> saves a risk assessment, SOP, checklist or manual from one of your vessels; then send it to the others.</div>
          : !shown.length ? <div style={{ color: C.muted, fontSize: 12.5, padding: '16px 6px' }}>No standard in this filter.</div>
          : <>
            <div className="hcs-row hcs-head" style={{ '--cols': COLS }}><div>Standard</div>{fleet.map(v => <div key={v.id}>{v.vessel_name}</div>)}</div>
            {shown.map(r => {
              const s = r.standard, last = lastOf(s.id)
              return (
                <div key={s.id} className="hcs-row" style={{ '--cols': COLS }} data-standard={s.code || s.id}>
                  <div style={{ minWidth: 0 }}>
                    <div className="hcs-name">{s.code && <span style={{ color: C.muted, fontWeight: 700, marginRight: 6 }}>{s.code}</span>}{s.title}</div>
                    <div className="hcs-sub">{KIND_LABEL[s.kind]} &middot; v{s.current_version} &middot; <b style={{ color: r.adopted === r.of ? C.green : C.text }}>{r.adopted} of {r.of}</b> active on board{last && last.note ? ' · ' + last.note : ''}</div>
                    <div className="hcs-acts">
                      <button className="hcs-ghost" onClick={() => { setNote(''); setPanel({ type: 'send', std: s }) }}>Send to vessels</button>
                      <button className="hcs-ghost" onClick={() => { setNote(''); setPanel({ type: 'save', std: s }) }}>New version</button>
                    </div>
                  </div>
                  <div className="hcs-cells">
                    {fleet.map(v => {
                      const c = r.cells[v.id], st = STATUS[c.status], col = T[st.tone]
                      return (
                        <button key={v.id} className="hcs-cell" data-cell={c.status} onClick={() => openCell(v, r, c)}
                          title={c.copy ? 'Open ' + v.vessel_name + "'s copy" : 'Send to ' + v.vessel_name}>
                          <span className="hcs-vn">{v.vessel_name}</span>
                          <span className="hcs-pill" style={{ background: col + '22', color: col }}><i style={{ background: col }} />{st.label}</span>
                          {(c.detail || c.outdated) && <small>{c.detail}</small>}
                        </button>)
                    })}
                  </div>
                </div>)
            })}
          </>}
      </div>
    </div>
  )
}
